import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import { LoaderFunctionArgs, redirect } from "react-router-dom";
import { apiClient, UnitTableLoaderResult } from "~/services/apiClient";
import { LoaderData, FormattedUnitData } from "~/types";
import { getCachedAuthAccessInfo, loadCookiesData } from "~/utils";
import {
  createClientFilteredShouldRevalidate,
  setLoaderQueryData,
  STALE_TIME_TIER_A,
} from "~/utils/loaderQueryCache";
import * as APITypes from "~/API";

export type UnitTablePageLoaderType = {
  unitList: LoaderData<FormattedUnitData[]>;
  propertyList: LoaderData<APITypes.Property[]>;
  isAuthenticated: boolean;
};

export function unitTableQueryKey(
  ownerID: string,
  ownerType: APITypes.OwnerType | "",
) {
  return ["unit-table", ownerID, ownerType] as const;
}

export const unitTableShouldRevalidate = createClientFilteredShouldRevalidate(
  (owner) => unitTableQueryKey(owner.ownerID, owner.ownerType),
  STALE_TIME_TIER_A,
);

export async function fetchUnitTableData(): Promise<UnitTablePageLoaderType> {
  const user = await getReadyAuthUser();

  if (!user) {
    return {
      unitList: { success: true, data: [] },
      propertyList: { success: true, data: [] },
      isAuthenticated: false,
    };
  }

  const authAccessInfo = await loadCookiesData(user.uid);
  if (!authAccessInfo.success) {
    return {
      unitList: { success: true, data: [] },
      propertyList: { success: true, data: [] },
      isAuthenticated: false,
    };
  }

  const { ownerID, ownerType } = authAccessInfo.data;

  const result = await apiClient.loader<UnitTableLoaderResult>("unit-table", {
    ownerID,
    ownerType,
  });

  const unitListData: FormattedUnitData[] = (result.data?.unitList ?? []).map(
    ({ unitDetails, buildingDetails, propertyDetails }) => ({
      unitDetails,
      buildingDetails,
      propertyDetails,
    }),
  );

  return {
    unitList: {
      success: result.success,
      data: unitListData,
    },
    propertyList: {
      success: true,
      data: result.data?.propertyList ?? [],
    },
    isAuthenticated: true,
  };
}

export async function UnitTablePageLoader({
  request,
}: LoaderFunctionArgs): Promise<UnitTablePageLoaderType | Response> {
  void request;
  const data = await fetchUnitTableData();
  const auth = getCachedAuthAccessInfo();

  if (auth) {
    setLoaderQueryData(
      unitTableQueryKey(auth.ownerID, auth.ownerType),
      data,
      STALE_TIME_TIER_A,
    );
  } else {
    setLoaderQueryData(unitTableQueryKey("", ""), data, STALE_TIME_TIER_A);
  }

  return data;
}
